import type React from 'react';

import { useCallback, useEffect, useRef } from 'react';

import { TranscriptCue } from '../player/player.interface';

export function useSyncWithVideo({
  currentActiveCue,
  cues,
  scrollToTranscriptItem,
  scrollRef,
  shown,
  autoScrollEnabled,
  setAutoScrollEnabled,
}: {
  currentActiveCue: TranscriptCue | undefined;
  cues: TranscriptCue[] | undefined;
  scrollToTranscriptItem: (index: number) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
  shown?: boolean;
  autoScrollEnabled: boolean;
  setAutoScrollEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  // Scroll and detect scroll end
  // We don't have a reliable scrollend event, so we use a timeout to detect when the scrolling has stopped
  const scrollWasInitiatedByOurCode = useRef(false);
  const AUTO_SCROLL_TIMEOUT = 250; // This timeout needs to be big enough for all browsers (some trigger scroll events more frequently than others), but not too big to prevent auto-scroll disable when user scrolls manually to not work
  const scrollToTranscriptItemWithSignal = useCallback(
    (index: number) => {
      const scrollable = scrollRef.current;
      let timeout: NodeJS.Timeout | undefined = undefined;
      const onScroll = () => {
        if (timeout) {
          clearTimeout(timeout);
        }
        scrollTimeout();
      };
      const scrollTimeout = () => {
        timeout = setTimeout(() => {
          scrollable?.removeEventListener('scroll', onScroll);
          scrollWasInitiatedByOurCode.current = false;
        }, AUTO_SCROLL_TIMEOUT);
      };
      scrollable?.addEventListener('scroll', onScroll, { passive: true });
      scrollWasInitiatedByOurCode.current = true;
      scrollToTranscriptItem(index);
      // Fallback in case the scroll event is not triggered
      scrollTimeout();

      return () => clearTimeout(timeout);
    },
    [scrollRef, scrollToTranscriptItem]
  );

  // Sync with video
  const syncWithVideo = useCallback(() => {
    setAutoScrollEnabled(true);
  }, [setAutoScrollEnabled]);

  // Auto-scroll behaviour
  useEffect(() => {
    if (currentActiveCue && cues?.length && autoScrollEnabled && shown) {
      return scrollToTranscriptItemWithSignal(cues?.indexOf(currentActiveCue) ?? 0);
    }
  }, [autoScrollEnabled, cues, currentActiveCue, scrollToTranscriptItemWithSignal, shown]);

  // Detect if user has manually scrolled, and disable auto-scroll if so
  useEffect(() => {
    if (autoScrollEnabled) {
      const onScroll = () => {
        if (!scrollWasInitiatedByOurCode.current) {
          setAutoScrollEnabled(false);
          scrollable?.removeEventListener('scroll', onScroll);
        }
      };
      const scrollable = scrollRef.current;
      scrollable?.addEventListener('scroll', onScroll, { passive: true });

      return () => {
        scrollable?.removeEventListener('scroll', onScroll);
      };
    }
  }, [autoScrollEnabled, scrollRef, setAutoScrollEnabled]);

  // When shown, enable auto-scroll
  // A useEffect is needed as some browers initially scroll, thus disabling auto-scroll on mount (even if that is the default state)
  useEffect(() => {
    if (shown) {
      setAutoScrollEnabled(true);
    }
  }, [setAutoScrollEnabled, shown]);

  return { syncWithVideoEnabled: !autoScrollEnabled, syncWithVideo };
}
